/* Players for the harness. All deterministic given the seed. */
import { MAX_PIECES, N, OCTAGON, WHITE, applyMove, candidateMoves, countAlive, decisiveResult, other } from '../../engine/src/index.ts';
import type { Game, LegalMove, Position, Rules, Side } from '../../engine/src/index.ts';

export type Rng = () => number;
export type Chooser = (g: Game, moves: LegalMove[], rnd: Rng) => LegalMove;

export function makeRng(seed: number): Rng {
  let s = seed >>> 0 || 1;
  return () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
}

const onEdge = (r: number, c: number): boolean => r === 0 || r === N - 1 || c === 0 || c === N - 1;

/** Static evaluation from `me`'s point of view. Material dominates; small nudges for advancement, edges and Core safety. */
export function evaluate(pos: Position, me: Side, rules: Rules): number {
  const res = decisiveResult(pos, rules);
  if (res) return res.winner === me ? 1e6 : res.winner === null ? 0 : -1e6;
  let v = (countAlive(pos, me) - countAlive(pos, other(me))) * 10;
  for (let i = 0; i < MAX_PIECES; i++) {
    if (!pos.alive[i]) continue;
    const r = pos.row[i], c = pos.col[i], mine = pos.owner[i] === me, edge = onEdge(r, c), core = pos.shape[i] === OCTAGON;
    if (mine) { v += (me === WHITE ? r : N - 1 - r) * 0.05; if (edge) v -= core ? 2 : 0.3; }
    else if (edge) v += core ? 2 : 0.3;
  }
  return v;
}

export const randomPlayer: Chooser = (_g, moves, rnd) => moves[Math.floor(rnd() * moves.length)];

/** One ply: the move whose immediate outcome evaluates best. Weak on purpose; it never gives material away, which exposes stalling. */
export const greedyPlayer: Chooser = (g, moves, rnd) => {
  const me = g.pos.toMove;
  let best = -Infinity, pick = moves[0];
  for (const lm of moves) {
    const v = evaluate(lm.pos, me, g.rules) + rnd() * 0.01;
    if (v > best) { best = v; pick = lm; }
  }
  return pick;
};

/** Two plies: my move, then the opponent's best reply by the same evaluation (ko ignored for the reply). */
export const minimax2Player: Chooser = (g, moves, rnd) => {
  const me = g.pos.toMove;
  let best = -Infinity, pick = moves[0];
  for (const lm of moves) {
    let worst = evaluate(lm.pos, me, g.rules);
    if (Math.abs(worst) < 1e5) {
      const replies = candidateMoves(lm.pos);
      if (replies.length) {
        worst = Infinity;
        for (const reply of replies) {
          const v = evaluate(applyMove(lm.pos, reply).pos, me, g.rules);
          if (v < worst) worst = v;
          if (worst < best) break;
        }
      }
    }
    const v = worst + rnd() * 0.01;
    if (v > best) { best = v; pick = lm; }
  }
  return pick;
};

export const PLAYERS: Record<string, Chooser> = { random: randomPlayer, greedy: greedyPlayer, minimax2: minimax2Player };
